import { storeManagerEndpoints } from '@/lib/endpoints';
import { CommodityGroup, type CgLabel, type CgMetadata, type CgNetwork } from '@whiz-cart/node-shared/models/commodityGroup/commodityGroup';
import { extendCommodityGroup, type CommodityGroupExtended } from '@whiz-cart/node-shared/models/commodityGroup/commodityGroupExtended';
import type { CommodityGroupFilter } from '@whiz-cart/node-shared/models/commodityGroup/commodityGroupFilter';
import Queue from '@whiz-cart/node-shared/queue';
import { service } from '@whiz-cart/node-shared/service/service';
import { createCache } from 'cross-state';

export default service(
    'cgService',
    class CGService {
        private q = new Queue();

        commodityGroups = createCache<CommodityGroupExtended[], [CommodityGroupFilter?]>(
            async (filter) => {
                let groups: CommodityGroup[] = [];

                if (filter?.storeGuid) {
                    groups = await (
                        filter.roots
                            ? storeManagerEndpoints.commodityGroups.cgStoreMergedRoots
                            : filter.leaves
                              ? storeManagerEndpoints.commodityGroups.cgStoreMergedLeaves
                              : storeManagerEndpoints.commodityGroups.cgStoreMerged
                    )({ storeGuid: filter.storeGuid }).fetch({
                        query: {
                            extended: filter.extended ?? false,
                        },
                    });
                }

                if (filter?.inventoryManagementSystem) {
                    groups = await storeManagerEndpoints.commodityGroups
                        .cgImsMerged({
                            inventoryManagementSystem: filter.inventoryManagementSystem,
                        })
                        .fetch({
                            query: {
                                extended: filter.extended ?? false,
                            },
                        });
                }

                if (filter?.extended) {
                    return groups.map((cg) => extendCommodityGroup(cg, filter.storeGuid));
                }

                return groups;
            },
            {
                invalidateOnWindowFocus: false,
                getCacheKey(filter?: CommodityGroupFilter) {
                    return {
                        storeGuid: filter?.storeGuid,
                        roots: filter?.roots ?? false,
                        leaves: filter?.leaves ?? false,
                        inventoryManagementSystem: filter?.inventoryManagementSystem,
                        extended: filter?.extended ?? false,
                    };
                },
            },
        );

        commodityGroupsLookup = this.commodityGroups.mapCache((hierarchy) => new Map(hierarchy.map((cg) => [cg.fullId, cg])));

        commodityGroupsLastModified = this.commodityGroups.mapCache((hierarchy) =>
            hierarchy.reduce<Date | undefined>((latest, cg) => {
                const date = cg.modifiedOn ?? cg.createdOn;
                return date && (!latest || date > latest) ? date : latest;
            }, undefined),
        );

        cgHierarchyHeaders = createCache(async () => {
            return await storeManagerEndpoints.commodityGroups.getCgHierarchyHeaders().fetch();
        });

        async updateMetadata(ims: number, storeGuid: string | undefined, fullId: string, metadata: CgMetadata) {
            if (storeGuid) {
                await storeManagerEndpoints.commodityGroups.cgStoreUpdateMetadata({ storeGuid, fullId }).fetch(metadata);
            } else {
                await storeManagerEndpoints.commodityGroups.cgImsUpdateMetadata({ inventoryManagementSystem: ims, fullId }).fetch(metadata);
            }
        }

        async updateLabels(ims: number, fullId: string, labels: Record<string, CgLabel>) {
            await this.q.schedule(async () => {
                await storeManagerEndpoints.commodityGroups
                    .setLabels({
                        fullId,
                        inventoryManagementSystem: ims,
                    })
                    .fetch(Object.keys(labels));
            });
        }

        async updateClassificationNetworks(ims: number, fullId: string, classificationNetworks: Record<string, CgNetwork>) {
            await this.q.schedule(async () => {
                await storeManagerEndpoints.commodityGroups
                    .setClassificationNetworks({
                        fullId,
                        inventoryManagementSystem: ims,
                    })
                    .fetch(classificationNetworks);
            });
        }

        async partialUpdate(ims: number, storeGuid: string | undefined, fullId: string) {
            try {
                await this.q.schedule(async () => {
                    let res;

                    if (storeGuid) {
                        res = await storeManagerEndpoints.commodityGroups
                            .cgStoreMergedPartial({
                                fullId,
                                storeGuid,
                            })
                            .fetch({ query: { extended: true } });
                    } else {
                        res = await storeManagerEndpoints.commodityGroups
                            .cgImsMergedPartial({
                                fullId,
                                inventoryManagementSystem: ims,
                            })
                            .fetch({ query: { extended: true } });
                    }

                    const updatedItems = res.map((cg) => extendCommodityGroup(cg, storeGuid));
                    const lookup = new Map(updatedItems.map((cg) => [cg.fullId, cg]));

                    for (const cache of this.commodityGroups.getInstances()) {
                        const { status, value } = cache.state.get();

                        const cacheFilter = cache.args[0];

                        if (
                            (storeGuid && cacheFilter?.storeGuid !== storeGuid) ||
                            (!storeGuid && cacheFilter?.inventoryManagementSystem !== ims) ||
                            status !== 'value'
                        ) {
                            cache.clear();
                            continue;
                        }

                        let found = false;
                        const updatedValue = value.map((item) => {
                            let match;
                            if ((match = lookup.get(item.fullId))) {
                                found = true;
                                return match;
                            } else {
                                return item;
                            }
                        });

                        if (found) {
                            cache.updateValue(updatedValue);
                        }
                    }
                });
            } catch (error) {
                this.commodityGroups.invalidateAll();
                throw error;
            }
        }
    },
);
